import React, { useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Search, Save, X, ArrowUp, ArrowDown, BookOpen } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';
import { adminCreateGuide, adminUpdateGuide, adminDeleteGuide } from '../lib/api';

const EMPTY_SECTION = { heading: '', body: '' };

const EMPTY_GUIDE = {
  category: '',
  title: '',
  excerpt: '',
  readTime: '5 min read',
  sections: [{ ...EMPTY_SECTION }],
};

const submitLabel = (isSaving, isNew) => {
  if (isSaving) return 'Saving\u2026';
  if (isNew) return 'Add guide';
  return 'Save changes';
};

const GuidesTab = ({ passcode, guides, onChange }) => {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // guide object OR 'new'
  const [form, setForm] = useState(EMPTY_GUIDE);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guides;
    return guides.filter((g) =>
      [g.title, g.category, g.excerpt].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [guides, query]);

  const startNew = () => {
    setForm({ ...EMPTY_GUIDE, sections: [{ ...EMPTY_SECTION }] });
    setEditing('new');
  };

  const startEdit = (g) => {
    setForm({
      category: g.category || '',
      title: g.title || '',
      excerpt: g.excerpt || '',
      readTime: g.readTime || '5 min read',
      sections: Array.isArray(g.sections) && g.sections.length
        ? g.sections.map((s) => ({ heading: s.heading || '', body: s.body || '' }))
        : [{ ...EMPTY_SECTION }],
    });
    setEditing(g);
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
    setForm(EMPTY_GUIDE);
  };

  const updateSection = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => (i === index ? { ...s, [key]: value } : s)),
    }));
  };

  const addSection = () => {
    setForm((prev) => ({ ...prev, sections: [...prev.sections, { ...EMPTY_SECTION }] }));
  };

  const removeSection = (index) => {
    setForm((prev) => {
      if (prev.sections.length <= 1) return prev;
      return { ...prev, sections: prev.sections.filter((_, i) => i !== index) };
    });
  };

  const moveSection = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.sections];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, sections: next };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category.trim()) {
      toast.error('Title and category are required.');
      return;
    }
    const cleanSections = form.sections
      .map((s) => ({ heading: s.heading.trim(), body: s.body.trim() }))
      .filter((s) => s.heading || s.body);
    if (cleanSections.length === 0) {
      toast.error('Add at least one section (heading + body).');
      return;
    }
    const invalid = cleanSections.find((s) => !s.heading || !s.body);
    if (invalid) {
      toast.error('Every section needs both a heading and body text.');
      return;
    }
    const payload = {
      category: form.category.trim(),
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      readTime: form.readTime.trim() || '5 min read',
      sections: cleanSections,
    };
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await adminCreateGuide(passcode, payload);
        onChange([created, ...guides]);
        toast.success('Guide published');
      } else {
        const updated = await adminUpdateGuide(passcode, editing.id, payload);
        onChange(guides.map((g) => (g.id === editing.id ? updated : g)));
        toast.success('Guide updated');
      }
      setEditing(null);
      setForm(EMPTY_GUIDE);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save guide.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await adminDeleteGuide(passcode, confirmDelete.id);
      onChange(guides.filter((g) => g.id !== confirmDelete.id));
      toast.success(`"${confirmDelete.title}" removed`);
      setConfirmDelete(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete guide.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, category, or excerpt…"
            className="pl-10 h-11 border-[#E8DCC0] bg-white"
          />
        </div>
        <button
          onClick={startNew}
          className="btn-orange inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Add guide
        </button>
      </div>

      <div className="bg-white border border-[#E8DCC0] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#EDE3CC] text-[#0F1B2D]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Title</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Sections</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Read time</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g, idx) => (
                <tr key={g.id} className={`border-t border-[#F0E7CE] ${idx % 2 ? 'bg-[#FCF8EE]' : 'bg-white'}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F1B2D] max-w-md">{g.title}</div>
                    {g.excerpt ? (
                      <div className="text-xs text-[#5a5a5a] mt-1 line-clamp-2 max-w-md">{g.excerpt}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#3a3a3a]">{g.category}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-[#3a3a3a]">
                    <span className="inline-flex items-center gap-1 text-xs">
                      <BookOpen className="w-3.5 h-3.5" /> {(g.sections || []).length}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-[#3a3a3a]">{g.readTime}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => startEdit(g)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#EDE3CC] transition-colors text-xs"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(g)}
                        disabled={deletingId === g.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition-colors text-xs disabled:opacity-60"
                      >
                        {deletingId === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-[#5a5a5a]">No guides match.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEditor(); }}>
        <DialogContent className="sm:max-w-3xl bg-[#F8F2E4] border border-[#E8DCC0] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-besley text-2xl text-[#0F1B2D]">
              {editing === 'new' ? 'Add legal guide' : `Edit: ${editing?.title || 'guide'}`}
            </DialogTitle>
            <DialogDescription className="text-[#5a5a5a]">
              A guide is made of a title, category, short excerpt, and one or more sections shown as the article body.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-[#0F1B2D]">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="e.g. Understanding maintenance under Section 125 CrPC" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Category *</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="Family Law" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Read time</Label>
                <Input value={form.readTime} onChange={(e) => setForm({ ...form, readTime: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="5 min read" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#0F1B2D]">Excerpt</Label>
                <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1.5 border-[#E8DCC0] bg-white" placeholder="One-line summary shown on the guide card." />
              </div>
            </div>

            <div className="pt-3 border-t border-[#E8DCC0]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-serif-besley text-lg text-[#0F1B2D]">Sections *</h3>
                  <p className="text-xs text-[#5a5a5a]">Each section shows as a heading and paragraph on the article page.</p>
                </div>
                <button
                  type="button"
                  onClick={addSection}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#EDE3CC] transition-colors text-xs"
                >
                  <Plus className="w-3.5 h-3.5" /> Add section
                </button>
              </div>

              <div className="space-y-3">
                {form.sections.map((section, index) => (
                  <div key={index} className="bg-white border border-[#E8DCC0] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#5a5a5a] font-mono">Section {index + 1} of {form.sections.length}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveSection(index, -1)}
                          disabled={index === 0}
                          className="p-1.5 rounded-md text-[#5a5a5a] hover:bg-[#EDE3CC] disabled:opacity-30 disabled:hover:bg-transparent"
                          aria-label="Move section up"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSection(index, 1)}
                          disabled={index === form.sections.length - 1}
                          className="p-1.5 rounded-md text-[#5a5a5a] hover:bg-[#EDE3CC] disabled:opacity-30 disabled:hover:bg-transparent"
                          aria-label="Move section down"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSection(index)}
                          disabled={form.sections.length <= 1}
                          className="p-1.5 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                          aria-label="Remove section"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Input
                        value={section.heading}
                        onChange={(e) => updateSection(index, 'heading', e.target.value)}
                        placeholder="Section heading"
                        className="h-10 border-[#E8DCC0]"
                      />
                      <Textarea
                        rows={4}
                        value={section.body}
                        onChange={(e) => updateSection(index, 'body', e.target.value)}
                        placeholder="Section body text — write in plain, jargon-free language."
                        className="border-[#E8DCC0]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2 sticky bottom-0 bg-[#F8F2E4] py-3">
              <button
                type="button"
                onClick={closeEditor}
                disabled={saving}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-[#D6CAB0] text-sm text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors disabled:opacity-60"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-orange inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-70"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {submitLabel(saving, editing === 'new')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &quot;{confirmDelete?.title}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the guide from the public /legal-guides directory. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Remove guide
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GuidesTab;
